import { Component } from '@theme/component';
import { deleteCookie, getCookie, setCookie } from '@theme/utilities';

/**
 * @typedef {Object} HeaderPromotionBannerRefs
 * @property {HTMLButtonElement} [dismissButton] - Close button element
 */

/**
 * Header promotion banner custom element.
 *
 * Handles dismissal state using cookies and hides the banner
 * when a valid dismiss cookie exists.
 *
 * @extends {Component<HeaderPromotionBannerRefs>}
 */
class HeaderPromotionBannerComponent extends Component {
  /**
   * Initializes component and applies persisted dismiss state.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#applyDismissState();
  }

  /**
   * Handles dismiss button action.
   */
  dismiss = () => {
    this.#hideBanner();
    this.#persistDismissState();
  };

  /**
   * Parsed dismiss duration in days (minimum 1).
   * @returns {number}
   */
  get #dismissDays() {
    const parsedDays = Number.parseInt(this.dataset.dismissDays ?? '', 10);
    return Number.isNaN(parsedDays) ? 7 : Math.max(parsedDays, 1);
  }

  /**
   * Dismiss cookie key.
   * @returns {string}
   */
  get #dismissKey() {
    return (this.dataset.dismissKey ?? '').trim();
  }

  /**
   * True when running in Shopify theme editor.
   * @returns {boolean}
   */
  get #isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  /**
   * Hides the promotion banner in the DOM.
   */
  #hideBanner() {
    const banner = this.closest('.promotion-banner');
    if (!(banner instanceof HTMLElement)) return;

    banner.setAttribute('aria-hidden', 'true');
  }

  /**
   * Applies persisted dismiss state from cookie.
   */
  #applyDismissState() {
    if (this.#isDesignMode) return;
    const dismissKey = this.#dismissKey;
    if (!dismissKey) return;

    try {
      const rawValue = getCookie(dismissKey);
      if (!rawValue) return;

      const expiresAt = Number.parseInt(rawValue, 10);

      if (Number.isNaN(expiresAt)) {
        deleteCookie(dismissKey);
        return;
      }

      if (expiresAt > Date.now()) {
        this.#hideBanner();
        return;
      }

      deleteCookie(dismissKey);
    } catch (error) {
      // Ignore cookie parsing errors to avoid blocking banner rendering.
    }
  }

  /**
   * Persists dismiss state in cookie.
   */
  #persistDismissState() {
    if (this.#isDesignMode) return;
    const dismissKey = this.#dismissKey;
    if (!dismissKey) return;

    const expiresAt = Date.now() + this.#dismissDays * 24 * 60 * 60 * 1000;

    try {
      setCookie(dismissKey, String(expiresAt), {
        maxAge: Math.max(this.#dismissDays, 1) * 24 * 60 * 60,
      });
    } catch (error) {
      // Ignore cookie write errors to keep close interaction functional.
    }
  }
}

if (!customElements.get('header-promotion-banner-component')) {
  customElements.define('header-promotion-banner-component', HeaderPromotionBannerComponent);
}
